import * as React from "react"
import { type IntervalOrigin } from "@digits-graphql/frontend/graphql-bearer"
import { SvgCalendar } from "@digits-shared/components/SVGIcons/line/Calendar.svg"
import { chevronStyles, PointingDirection } from "@digits-shared/components/UI/Elements/Chevron"
import { DigitsButton, type DigitsButtonProps } from "@digits-shared/DesignSystem/Button"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import dateTimeHelper, { DateFormat } from "@digits-shared/helpers/dateTimeHelper"
import { isMobile } from "@digits-shared/helpers/devicesHelper"
import stringHelper from "@digits-shared/helpers/stringHelper"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import { Desktop } from "src/frontend/components/Shared/Responsive/Desktop"
import { useTextIsOverflowing } from "src/frontend/hooks/useTextIsOverflowing"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

/*
  STYLES
*/

export const ActivePeriodContainer = styled.div`
  &.editable::after {
    ${chevronStyles(PointingDirection.Down, "4px", colors.secondary)};
    position: absolute;
    right: 9px;
    top: 50%;
    margin-top: -2px;
    transition: border-color 250ms ease-out;
  }

  position: relative;
  pointer-events: none;
  background: ${colors.transparent};
  font-weight: ${fonts.weight.book};
  font-size: 18px;
  color: ${colors.translucentSecondary80};
  border: none;
  padding: 7px 25px 7px 0;
  line-height: 1.5;
  transition:
    background-color 250ms ease,
    color 250ms ease;

  b {
    text-transform: capitalize;
    font-weight: ${fonts.weight.heavy};
    color: ${colors.secondary};
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.2;
  }

  &:focus {
    outline: none;
  }
`

const PeriodButton = styled(DigitsButton)`
  width: fill-available;
  border-radius: 0;
  border-left: 1px solid rgba(13, 13, 13, 0.1);
  border-right: 1px solid rgba(13, 13, 13, 0.1);

  span {
    text-overflow: ellipsis;
    overflow: hidden;
    font-weight: ${fonts.weight.normal} !important;
  }
`

const ToSpan = styled.span`
  text-transform: lowercase;
`

const FixedContainer = styled.span`
  &.month.default {
    min-width: 105px;
    &:has(${ToSpan}) {
      min-width: 230px;
    }
  }
`

/*
  INTERFACES
*/

interface PeriodProps {
  origin: IntervalOrigin
  dateFormat?: DateFormat
}

/*
  COMPONENTS
*/

export const ActivePeriod: React.FC<{
  editable: boolean
  activeOrigin?: IntervalOrigin
  className?: string
  children?: React.ReactNode
}> = ({ editable, activeOrigin, className, children }) => {
  const origin = useIntervalOrigin()
  const displayIntervalOrigin = activeOrigin || origin

  const classNames = stringHelper.classNames(className, editable && "editable")
  return (
    <ActivePeriodContainer className={classNames} color={colors.white}>
      {children}
      <PeriodName origin={displayIntervalOrigin} />
    </ActivePeriodContainer>
  )
}

export const ActivePeriodButton: React.FC<
  DigitsButtonProps & {
    activeOrigin?: IntervalOrigin
  }
> = ({ activeOrigin, $variant = "secondary-white", ...buttonProps }) => {
  const origin = useIntervalOrigin()
  const displayOrigin = activeOrigin || origin
  const intervalCount = displayOrigin.intervalCount ?? 0

  let format = DateFormat.Default
  switch (true) {
    case isMobile && intervalCount > 1:
      format = DateFormat.Micro
      break

    case isMobile:
      format = DateFormat.Medium
      break

    case intervalCount > 1:
      format = DateFormat.Macro
      break
  }
  const { isTextOverflowing, textElementRef } = useTextIsOverflowing<HTMLDivElement>()
  const rangeName = dateTimeHelper.displayNameFromIntervalOrigin(origin)

  const containerClassName = stringHelper.classNames(
    displayOrigin.interval.toLocaleLowerCase(),
    format.toLocaleLowerCase()
  )

  return (
    <DigitsTooltip content={rangeName} disabled={!isTextOverflowing}>
      <PeriodButton $variant={$variant} size="medium" {...buttonProps}>
        <Desktop>
          <SvgCalendar />
        </Desktop>

        <FixedContainer ref={textElementRef} className={containerClassName}>
          <PeriodName origin={displayOrigin} dateFormat={format} />
        </FixedContainer>
      </PeriodButton>
    </DigitsTooltip>
  )
}

const PeriodName: React.FC<PeriodProps> = ({ origin, dateFormat }) => {
  if (!origin.intervalCount || origin.intervalCount === 1) {
    return <IntervalName origin={origin} dateFormat={dateFormat} />
  }

  const priorPeriodOrigin = dateTimeHelper.addIntervalToOrigin(origin, -origin.intervalCount + 1)

  return (
    <>
      <IntervalName origin={priorPeriodOrigin} dateFormat={dateFormat} /> <ToSpan>to</ToSpan>{" "}
      <IntervalName origin={origin} dateFormat={dateFormat} />
    </>
  )
}

const IntervalName: React.FC<PeriodProps> = ({ origin, dateFormat }) => {
  const intervalParts = dateTimeHelper
    .displayNameFromIntervalOrigin({ ...origin, intervalCount: 1 }, dateFormat)
    .split(" ")

  if (intervalParts.length > 1) {
    return (
      <>
        <b>{intervalParts.slice(0, -1).join(" ")}&nbsp;</b>
        <span>{intervalParts[intervalParts.length - 1]}</span>
      </>
    )
  }
  return <b>{intervalParts.join(" ")}</b>
}
